import React from 'react';
import { Dropdown } from 'react-bootstrap';
import { FaTimes } from 'react-icons/fa';

export interface ISingleSelectOption {
    value: string;
    label: string;
    image?: string;
    highlight?: string;
}

interface ISingleSelectDropdown {
    placeholder: string;
    options: ISingleSelectOption[];
    selected: string;
    onChange: (selected: string) => void;
    withImage?: boolean;
    clearable?: boolean;
    active?: boolean;
}

export const toSelectValue = (value: unknown, emptyValue = ''): string =>
    value === undefined || value === null || String(value) === emptyValue ? '' : String(value);

export default function SingleSelectDropdown({
    placeholder,
    options,
    selected,
    onChange,
    withImage = false,
    clearable = true,
    active,
}: ISingleSelectDropdown) {
    const selectedOption = options.find(option => option.value === selected);
    const renderImage = (option: ISingleSelectOption) =>
        withImage && option.image ? (
            <img src={option.image} alt={option.label} width={20} className="rounded-1" />
        ) : null;

    return (
        <Dropdown>
            <Dropdown.Toggle
                variant=""
                className={`w-100 text-start border bg-white fs-7 ps-2 pe-2 d-flex align-items-center ${
                    active ?? !!selectedOption ? 'border-primary' : ''
                }`}
                style={{ color: selectedOption ? undefined : '#6c757d' }}
            >
                <span className="d-inline-flex align-items-center gap-2 flex-grow-1 text-truncate">
                    {selectedOption ? (
                        <>
                            {renderImage(selectedOption)}
                            {selectedOption.label}
                            {selectedOption.highlight && (
                                <span className="text-primary">{selectedOption.highlight}</span>
                            )}
                        </>
                    ) : (
                        placeholder
                    )}
                </span>
                {selectedOption && clearable && (
                    <span
                        role="button"
                        aria-label="Clear"
                        className="d-inline-flex align-items-center text-muted px-1 me-1"
                        onClick={event => {
                            event.stopPropagation();
                            onChange('');
                        }}
                    >
                        <FaTimes size={12} />
                    </span>
                )}
            </Dropdown.Toggle>
            <Dropdown.Menu className="w-100 py-1 fs-7" style={{ maxHeight: 300, overflowY: 'auto' }}>
                {options.map(option => (
                    <Dropdown.Item
                        key={option.value}
                        as="button"
                        type="button"
                        className={`py-1 px-2 d-flex align-items-center gap-2 ${
                            option.value === selected ? 'bg-primary-subtle text-primary' : ''
                        }`}
                        onClick={() => onChange(option.value)}
                    >
                        {renderImage(option)}
                        {option.label}
                        {option.highlight && <span className="text-primary">{option.highlight}</span>}
                    </Dropdown.Item>
                ))}
            </Dropdown.Menu>
        </Dropdown>
    );
}
